import { ApolloError } from '@apollo/client';
import { ErrorObject } from '@thoughtspot/blink-context';
import { translate } from '@thoughtspot/i18n';
import { ChipInputProps } from '@thoughtspot/radiant-react/widgets/chip/chip-input';
import {
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import {
    OptionsGroup,
    SelectOption,
} from '@thoughtspot/radiant-react/widgets/select/base-select';
import { ButtonCellProps } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/button-cell/button-cell';
import { RowDataProps } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/cell-type-service';
import { DropdownCellProps } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/dropdown-cell/dropdown-cell';
import _ from 'lodash';
import { AccessMode, Sharable } from '/@services/generated/graphql-types';
import { getTranslatedInfoForMessageCode } from '/@utils/error-handling/error-strings-mapping';
import { getErrorObjectFromGraphQLErrors } from '/@utils/error-handling/error-utils';
import { InviteButtonState, UserEmailProps } from './invite-users-v2.interface';

let permissionMap: Map<string, AccessMode>;

let inviteButton: InviteButtonState;

export const getInviteButtonState = (): InviteButtonState => inviteButton;

export const setInviteButtonState = (inviteButtonState: InviteButtonState) => {
    inviteButton = inviteButtonState;
};

/**
 * Initialize permission map
 */
export const initPermissionMap = () => {
    permissionMap = new Map<string, AccessMode>();
};

/**
 * Update liveboard permission map for the liveboardId
 * @param liveboardId
 * @param permission
 */
export const updateLiveboardPermission = (
    liveboardId: string,
    permission: AccessMode,
) => {
    if (_.isUndefined(permissionMap)) {
        initPermissionMap();
    }
    permissionMap.set(liveboardId, permission);
};

/**
 * Get liveboard permission for given liveboardId.
 * @param liveboardId
 * @returns
 */
export const getLiveboardPermission = (liveboardId: string): AccessMode => {
    return permissionMap.get(liveboardId);
};

export const getPermisionOptions = (
    userPermission: AccessMode,
    liveboardId: string,
): OptionsGroup[] => {
    const permissionOptions = [
        {
            title: '',
            options: [
                {
                    id: AccessMode.ReadOnly,
                    label: translate('sharePanel.READ_ONLY'),
                    data: liveboardId,
                },
            ],
        },
    ];
    if (userPermission === AccessMode.Modify) {
        permissionOptions[0].options.push({
            id: AccessMode.Modify,
            label: translate('sharePanel.MODIFY'),
            data: liveboardId,
        });
    }
    return permissionOptions;
};

/**
 * Convert emails to email chip format
 * @param email - email to convert to chip
 * @param postFixId - postFixId
 * @returns - Email chip
 */
export const getUserChipFromEmail = (
    email: UserEmailProps,
    postFixId: number,
): ChipInputProps => {
    const avatarConfig = !email.hasError
        ? {
              src: '',
              name: email.emailId,
              showName: false,
          }
        : null;
    const metaIconConfig = email.hasError
        ? {
              id: IconID.EXCLAMATION_POINT_CIRCLE,
              color: IconColor.RED,
              size: IconSize.XLARGE,
              tooltip: email.errorString,
          }
        : null;
    const chipInput: ChipInputProps = {
        content: email.emailId,
        icon: IconID.CROSS,
        avatarConfig,
        metaIconConfig,
        id: `${email.emailId}-${postFixId}`,
    };
    return chipInput;
};

/**
 * Util to convert sharable data into format that in can render in table list
 * @param objectList -  list of sharables
 * @param onDataSourceClicked - callback for data source clicked
 * @returns -  list of rowdataprops
 */
export const getTableDataFromSharables = (
    objectList: Sharable[],
    onDataSourceClicked: any,
) => {
    const sharableTables: any[] = [];
    objectList.forEach(object => {
        // data sources config
        const dataSourcesString =
            object.datasourceIds?.length > 1
                ? translate('invite.share.modal.dataSourcesMultiple', {
                      num: object.datasourceIds.length,
                  })
                : translate('invite.share.modal.dataSourcesSingle', {
                      num: object.datasourceIds.length,
                  });
        const onClick = () =>
            onDataSourceClicked(object.datasourceIds, object.name);

        // Permission dropdown config
        const onPermissionUpdate = (data: SelectOption) => {
            updateLiveboardPermission(data.data, data.id as AccessMode);
        };
        // Setting default all liveboard permission as read only
        updateLiveboardPermission(object.id, AccessMode.ReadOnly);

        const rowData = {
            id: object.id,
            liveboard: { text: object.name },
            permission: {
                id: `test-${object.id}`,
                optionsGroups: getPermisionOptions(object.shareMode, object.id),
                hasGrayBackground: true,
                onChange: onPermissionUpdate,
                selectedOptions: [AccessMode.ReadOnly],
            } as DropdownCellProps,
            dataSources: {
                text: dataSourcesString,
                type: 'tertiary',
                size: 'm',
                onClick,
            } as ButtonCellProps,
        };
        sharableTables.push(rowData);
    });
    return sharableTables;
};

export const normalizeData = (data: RowDataProps[]) => {
    return data?.map(el => {
        return {
            id: el.id,
            dataSource: el.name,
            creator: el.authorDisplayName,
            description: el.description ? el.description : 'N/A',
        };
    });
};

export const getEmailError = (
    error: ApolloError,
    isInvalidEmailFromRegex = false,
): string => {
    let errorMessage = translate('invite.email.genericError');
    const errorObjects: ErrorObject[] = getErrorObjectFromGraphQLErrors(error);
    const errorCode = errorObjects[0]?.errorObj?.extensions?.exception?.upstreamResponse?.status?.toString();
    if (errorCode) {
        switch (errorCode) {
            case '400':
                errorMessage = translate('homehub.invite_others.invalid_email');
                break;
            case '409':
                errorMessage = translate('homehub.invite_others.email_exists');
                break;
            default:
                errorMessage = getTranslatedInfoForMessageCode(
                    errorCode,
                    translate('invite.email.genericError'),
                )?.summary;
                break;
        }
    }
    if (isInvalidEmailFromRegex) {
        errorMessage = translate('homehub.invite_others.invalid_email');
    }
    return errorMessage;
};

export const getValidEmailIds = (emailChips: ChipInputProps[]): string[] => {
    return emailChips
        .filter(emailChip => emailChip.metaIconConfig === null)
        .map(emailChip => emailChip.content);
};

export const getUniqueDataSourceIds = (sharables: Sharable[]) => {
    const uniqueDatasources = new Set<string>();
    sharables.flatMap((sharable: Sharable) =>
        sharable.datasourceIds.forEach(
            uniqueDatasources.add,
            uniqueDatasources,
        ),
    );
    return uniqueDatasources;
};
